import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { acceptLegal, type LegalDocumentType } from '@/services/legal';

export interface OutstandingLegalDoc {
  document_type: LegalDocumentType;
  title: string;
  slug: string;
  version: number;
}

/**
 * Returns published legal documents the current user has not yet accepted for
 * the currently published version. Powered by the SECURITY DEFINER RPC
 * `legal_get_outstanding_acceptances` so users do not need direct access to
 * `legal_documents`.
 */
export function useOutstandingLegalAcceptances() {
  const { user } = useAuth();
  return useQuery<OutstandingLegalDoc[]>({
    queryKey: ['legal-outstanding', user?.id ?? null],
    enabled: !!user,
    staleTime: 60_000,
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase.rpc('legal_get_outstanding_acceptances');
      if (error) throw error;
      return (data as OutstandingLegalDoc[] | null) ?? [];
    },
  });
}

export function useAcceptLegal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { document_type: LegalDocumentType; version: number }) =>
      acceptLegal(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['legal-outstanding'] });
    },
  });
}